import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit2, Trash2, Save, X, Mail, Loader, Download, Upload, Tag, Copy, ChevronDown, ChevronUp, Search, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../../config/api';
import UnlayerEditor from '../../components/UnlayerEditor';
import Modal from '../../components/Modal';

const AdminBrandTemplatesPage = () => {
    const [unlayerTemplates, setUnlayerTemplates] = useState([]);
    const [brandTemplates, setBrandTemplates] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [groups, setGroups] = useState([]);
    
    // Editor state
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedBrandId, setSelectedBrandId] = useState('');
    const [extractedTags, setExtractedTags] = useState([]);
    const [isImportedTemplatesCollapsed, setIsImportedTemplatesCollapsed] = useState(false);
    const [brandTemplateSearch, setBrandTemplateSearch] = useState('');
    const [deletingTemplateId, setDeletingTemplateId] = useState(null);
    
    const [templateForm, setTemplateForm] = useState({
        message_name: '',
        subject: '',
        from_name: '',
        from_address: '',
        message_type: 'sales',
        html_content: '',
        unlayer_json: null
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadUnlayerTemplates(),
                loadBrandTemplates(),
                loadBrands(),
                loadGroups()
            ]);
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadGroups = async () => {
        try {
            const result = await apiClient.get('/groups');
            setGroups(result || []);
            if (result && result.length > 0 && !selectedGroupId) {
                setSelectedGroupId(result[0].id);
            }
        } catch (error) {
            console.error('Failed to load groups:', error);
        }
    };

    const loadUnlayerTemplates = async () => {
        try {
            const result = await apiClient.get('/voicebox/unlayer-templates?converted=false');
            setUnlayerTemplates(result.templates || []);
        } catch (error) {
            console.error('Failed to load UnLayer templates:', error);
        }
    };

    const loadBrandTemplates = async () => {
        try {
            // Use brand_templates_only flag to filter server-side
            // This ensures we only get actual brand templates, not imported Unlayer templates
            const result = await apiClient.get('/contact-messages?brand_templates_only=true');
            setBrandTemplates(result || []);
        } catch (error) {
            console.error('Failed to load brand templates:', error);
        }
    };

    const loadBrands = async () => {
        try {
            const result = await apiClient.get('/brands');
            setBrands(result || []);
        } catch (error) {
            console.error('Failed to load brands:', error);
        }
    };

    const handleImportUnlayerTemplates = async () => {
        if (!selectedGroupId) {
            alert('Please select a dealership group first');
            return;
        }

        setImporting(true);
        try {
            // Use 10 minute timeout (600000ms) for import operation - can take several minutes
            const result = await apiClient.post(
                `/voicebox/groups/${selectedGroupId}/import-unlayer-templates`,
                { overwrite: false },
                {},
                600000 // 10 minutes timeout
            );
            alert(`Imported ${result.imported} template(s). ${result.skipped} skipped.`);
            loadUnlayerTemplates();
        } catch (error) {
            console.error('Failed to import templates:', error);
            alert('Failed to import templates: ' + (error.message || 'Unknown error'));
        } finally {
            setImporting(false);
        }
    };

    const handleCreateNew = () => {
        setEditingTemplate(null);
        setSelectedBrandId('');
        setExtractedTags([]);
        setTemplateForm({
            message_name: '',
            subject: '',
            from_name: '',
            from_address: '',
            message_type: 'sales',
            html_content: '',
            unlayer_json: null
        });
        setIsEditorOpen(true);
    };

    const handleEditForBrand = async (template) => {
        setSelectedBrandId(template.brand_id || '');
        setExtractedTags(template.available_tags || []);

        // Determine if this is an UnLayer imported template (needs full fetch) or a brand template
        const isUnlayerTemplate = template.converted_to_brand_template !== undefined ||
            (template.id && !template.is_brand_template && template.voicebox_message_id);

        let fullTemplate = template;

        if (isUnlayerTemplate) {
            try {
                fullTemplate = await apiClient.get(`/voicebox/unlayer-templates/${template.id}`);
            } catch (error) {
                console.error('Failed to load full template:', error);
                alert('Failed to load template: ' + (error.message || 'Unknown error'));
                return;
            }
        }

        setEditingTemplate(fullTemplate);

        let parsedUnlayerJson = fullTemplate.unlayer_json;
        if (typeof parsedUnlayerJson === 'string') {
            try {
                parsedUnlayerJson = JSON.parse(parsedUnlayerJson);
            } catch (e) {
                console.error('Failed to parse unlayer_json:', e);
                parsedUnlayerJson = null;
            }
        }
        if (parsedUnlayerJson !== null && typeof parsedUnlayerJson !== 'object') {
            parsedUnlayerJson = null;
        }

        setTemplateForm({
            message_name: fullTemplate.message_name || '',
            subject: fullTemplate.subject || '',
            from_name: fullTemplate.from_name || '',
            from_address: fullTemplate.from_address || '',
            message_type: fullTemplate.message_type?.toLowerCase() || 'sales',
            html_content: fullTemplate.html_content || '',
            unlayer_json: parsedUnlayerJson
        });
        setIsEditorOpen(true);
    };

    const handleUnlayerSave = ({ html, json, tags }) => {
        setTemplateForm(prev => ({
            ...prev,
            html_content: html,
            unlayer_json: json
        }));
        if (tags && tags.length > 0) {
            setExtractedTags(tags);
        }
    };

    const handleTagsExtracted = (tags) => {
        setExtractedTags(tags);
    };

    // Filter brand templates based on search
    const filteredBrandTemplates = useMemo(() => {
        if (!brandTemplateSearch.trim()) {
            return brandTemplates;
        }
        const searchLower = brandTemplateSearch.toLowerCase();
        return brandTemplates.filter(template => {
            const brand = brands.find(b => b.id === template.brand_id);
            return (
                template.message_name?.toLowerCase().includes(searchLower) ||
                template.subject?.toLowerCase().includes(searchLower) ||
                brand?.name?.toLowerCase().includes(searchLower) ||
                template.message_type?.toLowerCase().includes(searchLower)
            );
        });
    }, [brandTemplates, brandTemplateSearch, brands]);

    const handleSaveBrandTemplate = async () => {
        if (!selectedBrandId) {
            alert('Please select a brand');
            return;
        }

        if (!templateForm.message_name) {
            alert('Please enter a template name');
            return;
        }

        setSaving(true);
        try {
            // Check if this is an UnLayer template (from voicebox_unlayer_templates table)
            // UnLayer templates have converted_to_brand_template flag but their ID is in voicebox_unlayer_templates, not contact_messages
            const isUnlayerTemplate = editingTemplate?.converted_to_brand_template !== undefined || 
                                     (editingTemplate?.id && !editingTemplate?.is_brand_template && editingTemplate?.voicebox_message_id);
            
            if (isUnlayerTemplate) {
                // This is an UnLayer template - always use convert-to-brand endpoint
                // This endpoint handles both new conversions and updates to already-converted templates
                const result = await apiClient.post(`/voicebox/unlayer-templates/${editingTemplate.id}/convert-to-brand`, {
                    brand_id: selectedBrandId,
                    ...templateForm,
                    extracted_tags: extractedTags
                });
                alert('Template saved successfully!');
            } else if (editingTemplate?.is_brand_template && editingTemplate?.id) {
                // This is an existing brand template in contact_messages - update it
                const result = await apiClient.put(`/contact-messages/${editingTemplate.id}`, {
                    ...templateForm,
                    brand_id: selectedBrandId || editingTemplate.brand_id,
                    is_brand_template: true,
                    extracted_tags: extractedTags
                });
                alert('Brand template updated successfully!');
            } else {
                // Create new brand template
                const result = await apiClient.post('/brand-templates', {
                    ...templateForm,
                    brand_id: selectedBrandId,
                    is_brand_template: true,
                    extracted_tags: extractedTags
                });
                alert('Brand template created successfully!');
            }
            
            setIsEditorOpen(false);
            loadUnlayerTemplates();
            loadBrandTemplates();
        } catch (error) {
            console.error('Failed to save brand template:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
            alert('Failed to save template: ' + errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUnlayerTemplate = async (templateId, templateName) => {
        if (!window.confirm(`Are you sure you want to delete "${templateName}"? This will permanently remove it from the imported templates.`)) {
            return;
        }

        setDeletingTemplateId(templateId);
        try {
            await apiClient.delete(`/voicebox/unlayer-templates/${templateId}`);
            alert(`Template "${templateName}" deleted successfully`);
            loadUnlayerTemplates();
        } catch (error) {
            console.error('Failed to delete template:', error);
            alert('Failed to delete template: ' + (error.response?.data?.error || error.message || 'Unknown error'));
        } finally {
            setDeletingTemplateId(null);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <Tag size={32} />
                        Brand Template Management
                    </h1>
                    <p className="text-gray-600">
                        Create brand-specific templates (VW, Audi, SEAT, BYD, etc.) using UnLayer editor. Templates are stored in the same format as VoiceBox for full interoperability.
                    </p>
                </div>
                <button
                    onClick={handleCreateNew}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    <Plus size={18} />
                    Create Brand Template
                </button>
            </div>

            {/* Import Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Import VoiceBox UnLayer Templates</h2>
                <div className="flex items-center gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Dealership Group
                        </label>
                        <div className="relative">
                            <select
                                value={selectedGroupId || ''}
                                onChange={(e) => setSelectedGroupId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="">Select a group...</option>
                                {groups.map(group => {
                                    const isSynced = group.is_voicebox_synced === true;
                                    return (
                                        <option key={group.id} value={group.id}>
                                            {group.name}{!isSynced && ' (Not Synced)'}
                                        </option>
                                    );
                                })}
                            </select>
                            {selectedGroupId && (() => {
                                const selectedGroup = groups.find(g => g.id === selectedGroupId);
                                const isSynced = selectedGroup?.is_voicebox_synced === true;
                                return isSynced ? (
                                    <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                        <CheckCircle2 size={18} className="text-green-600" />
                                    </div>
                                ) : null;
                            })()}
                        </div>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={handleImportUnlayerTemplates}
                            disabled={!selectedGroupId || importing}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                        >
                            {importing ? (
                                <>
                                    <Loader className="animate-spin" size={16} />
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <Download size={16} />
                                    Import UnLayer Templates
                                </>
                            )}
                        </button>
                    </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                    This will import all VoiceBox messages that have UnLayer JSON (EmailContentJson). You can then edit them and convert to brand templates.
                </p>
            </div>

            {/* Brand Templates Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Brand Templates</h2>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={brandTemplateSearch}
                                onChange={(e) => setBrandTemplateSearch(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>
                {filteredBrandTemplates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        {brandTemplateSearch ? (
                            <p>No brand templates found matching "{brandTemplateSearch}"</p>
                        ) : (
                            <p>No brand templates created yet. Edit and convert UnLayer templates to create brand templates.</p>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredBrandTemplates.map((template) => {
                                    const brand = brands.find(b => b.id === template.brand_id);
                                    return (
                                        <tr key={template.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium text-gray-900">{template.message_name}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{brand?.name || '-'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600 capitalize">{template.message_type?.toLowerCase() || '-'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{template.subject || '-'}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleEditForBrand(template)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Edit template"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            if (window.confirm(`Duplicate "${template.message_name}"?`)) {
                                                                try {
                                                                    const result = await apiClient.post('/brand-templates', {
                                                                        ...template,
                                                                        message_name: `${template.message_name} (Copy)`,
                                                                        brand_id: template.brand_id,
                                                                        is_brand_template: true,
                                                                        extracted_tags: template.available_tags || []
                                                                    });
                                                                    alert('Template duplicated successfully!');
                                                                    loadBrandTemplates();
                                                                } catch (error) {
                                                                    alert('Failed to duplicate template: ' + (error.message || 'Unknown error'));
                                                                }
                                                            }
                                                        }}
                                                        className="text-purple-600 hover:text-purple-900"
                                                        title="Duplicate template"
                                                    >
                                                        <Copy size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* UnLayer Templates Section - Collapsible */}
            <div className="bg-white rounded-lg shadow-sm border">
                <button
                    onClick={() => setIsImportedTemplatesCollapsed(!isImportedTemplatesCollapsed)}
                    className="w-full flex justify-between items-center p-6 hover:bg-gray-50 transition-colors"
                >
                    <h2 className="text-xl font-semibold text-gray-900">Imported UnLayer Templates</h2>
                    {isImportedTemplatesCollapsed ? (
                        <ChevronDown className="text-gray-500" size={24} />
                    ) : (
                        <ChevronUp className="text-gray-500" size={24} />
                    )}
                </button>
                {!isImportedTemplatesCollapsed && (
                    <div className="px-6 pb-6">
                        {loading ? (
                            <div className="text-center py-8">
                                <Loader className="animate-spin mx-auto" size={32} />
                                <p className="text-gray-600 mt-4">Loading templates...</p>
                            </div>
                        ) : unlayerTemplates.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <Mail className="mx-auto text-gray-400" size={48} />
                                <p className="mt-4">No UnLayer templates imported yet. Import templates from VoiceBox to get started.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folder</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {unlayerTemplates.map((template) => (
                                            <tr key={template.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 font-medium text-gray-900">{template.message_name}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{template.subject || '-'}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{template.brand_name || '-'}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{template.folder_path || '-'}</td>
                                                <td className="px-6 py-4 text-sm">
                                                    {template.available_tags && Array.isArray(template.available_tags) ? (
                                                        <span className="text-xs text-gray-500">
                                                            {template.available_tags.length} tags
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleEditForBrand(template)}
                                                            className="text-blue-600 hover:text-blue-900"
                                                            title="Edit and convert to brand template"
                                                        >
                                                            <Edit2 size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteUnlayerTemplate(template.id, template.message_name)}
                                                            disabled={deletingTemplateId === template.id}
                                                            className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                                            title="Delete this imported template"
                                                        >
                                                            {deletingTemplateId === template.id ? (
                                                                <Loader className="animate-spin" size={18} />
                                                            ) : (
                                                                <Trash2 size={18} />
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* UnLayer Editor Modal */}
            {isEditorOpen && (
                <Modal
                    isOpen={isEditorOpen}
                    onClose={() => setIsEditorOpen(false)}
                    title={editingTemplate ? (editingTemplate.is_brand_template ? 'Edit Brand Template' : 'Edit Template for Brand Conversion') : 'Create Brand Template'}
                    size="2xl"
                    resizable={true}
                >
                    <div className="space-y-4">
                        {!editingTemplate && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-900">
                                    <strong>Create a new brand template:</strong> Use the UnLayer editor below to design your template. 
                                    Templates are saved in the same JSON format as VoiceBox for full compatibility.
                                </p>
                            </div>
                        )}
                        {editingTemplate && !editingTemplate.converted_to_brand_template && !editingTemplate.is_brand_template && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-sm text-blue-900">
                                    <strong>Step 1:</strong> Edit this template in UnLayer to strip it back to a brand-agnostic template.
                                    <br />
                                    <strong>Step 2:</strong> Select a brand and save to convert it to a brand template.
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Template Name *
                                </label>
                                <input
                                    type="text"
                                    value={templateForm.message_name}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, message_name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Subject Line
                                </label>
                                <input
                                    type="text"
                                    value={templateForm.subject}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    From Name
                                </label>
                                <input
                                    type="text"
                                    value={templateForm.from_name}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, from_name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    From Email
                                </label>
                                <input
                                    type="email"
                                    value={templateForm.from_address}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, from_address: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Select Brand *
                            </label>
                            <select
                                value={selectedBrandId || (editingTemplate?.brand_id || '')}
                                onChange={(e) => setSelectedBrandId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                disabled={editingTemplate?.is_brand_template && editingTemplate?.brand_id}
                            >
                                <option value="">Select a brand...</option>
                                {brands.map(brand => (
                                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                                ))}
                            </select>
                            {editingTemplate?.is_brand_template && editingTemplate?.brand_id && (
                                <p className="text-xs text-gray-500 mt-1">Brand cannot be changed for existing brand templates</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Content (UnLayer Editor)
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                                    Templates are saved in VoiceBox-compatible JSON format (EmailContentJson) for full interoperability.
                                </p>
                            <UnlayerEditor
                                key={`brand-template-editor-${editingTemplate?.id || 'new'}-${selectedBrandId || 'no-brand'}`}
                                design={templateForm.unlayer_json}
                                htmlContent={templateForm.html_content}
                                onSave={handleUnlayerSave}
                                onTagsExtracted={handleTagsExtracted}
                                displayMode="email"
                                editorId={`brand-template-editor-${editingTemplate?.id || 'new'}-${selectedBrandId || 'no-brand'}`}
                                showSaveButton={false}
                                mergeTags={[]}
                                availableFields={[]}
                            />
                            {extractedTags.length > 0 && (
                                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                    <p className="text-xs font-medium text-gray-900 mb-1">Detected Tags:</p>
                                    <div className="flex flex-wrap gap-1">
                                        {extractedTags.map((tag, idx) => (
                                            <span
                                                key={idx}
                                                className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs font-mono"
                                            >
                                                %%{tag}%%
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t">
                            <button
                                onClick={() => setIsEditorOpen(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveBrandTemplate}
                                disabled={saving || !templateForm.message_name || !selectedBrandId}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300"
                            >
                                {saving ? (
                                    <>
                                        <Loader className="animate-spin" size={16} />
                                        {editingTemplate ? 'Saving...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        {editingTemplate ? 'Save Brand Template' : 'Create Brand Template'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

        </div>
    );
};

export default AdminBrandTemplatesPage;
